import { useState, useCallback } from "react";
import api from "../api";

export function formatVerifierButtonLabel(myVerdicts, aggregate = null) {
  const web = myVerdicts?.web ?? null;
  const mobile = myVerdicts?.mobile ?? null;

  const webLabel = web === "approved" ? "Web ✓" : web === "rejected" ? "Web ✗" : web ? `Web ${web}` : null;
  const mobileLabel = mobile === "approved" ? "Mobile ✓" : mobile === "rejected" ? "Mobile ✗" : mobile ? `Mobile ${mobile}` : null;

  if (webLabel && mobileLabel) return `${webLabel} ${mobileLabel}`;
  if (webLabel) return webLabel;
  if (mobileLabel) return mobileLabel;

  if ((aggregate?.rejected ?? 0) > 0) return "Rejected";
  return "Verify";
}

export function getVerifierButtonColors(myVerdicts, aggregate = null) {
  const web = myVerdicts?.web ?? null;
  const mobile = myVerdicts?.mobile ?? null;

  if (web === "rejected" || mobile === "rejected" || (aggregate?.rejected ?? 0) > 0) {
    return { variant: "contained", bgcolor: "error.main", color: "white" };
  }
  if (web === "approved" && mobile === "approved") {
    return { variant: "contained", bgcolor: "success.main", color: "white" };
  }
  if (web || mobile) {
    return { variant: "contained", bgcolor: "warning.main", color: "white" };
  }
  return { variant: "outlined", bgcolor: "transparent", color: "info.main" };
}

export default function useContentVerification() {
  const [verificationsByItemKey, setVerificationsByItemKey] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const itemKey = (type, id) => `${type}:${id}`;

  const loadVerifications = useCallback(async (type, id) => {
    try {
      const res = await api.get(`admin/content-verifications/${type}/${id}`);
      if (res.data?.success) {
        setVerificationsByItemKey((prev) => ({
          ...prev,
          [itemKey(type, id)]: res.data.data,
        }));
      }
    } catch {
      // non-fatal
    }
  }, []);

  const submitVerification = useCallback(async ({ type, id, status, notes, platforms, platform }) => {
    setSubmitting(true);
    try {
      const payload = {
        verifiable_type: type,
        verifiable_id: id,
        status,
        notes: notes || null,
      };
      if (platforms?.length) {
        payload.platforms = platforms;
      } else if (platform) {
        payload.platform = platform;
      }

      const res = await api.post("admin/content-verifications", payload);
      if (res.data?.success) {
        const key = itemKey(type, id);
        const data = res.data.data;
        setVerificationsByItemKey((prev) => ({
          ...prev,
          [key]: {
            ...(prev[key] ?? {}),
            verifications: data.verifications ?? prev[key]?.verifications ?? [],
            aggregate: data.aggregate,
            my_verdicts: data.my_verdicts ?? prev[key]?.my_verdicts ?? {},
            my_verdict: data.my_verdict ?? status,
          },
        }));
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      setSubmitting(false);
    }
  }, []);

  const getItemData = useCallback((type, id) => {
    return verificationsByItemKey[itemKey(type, id)] ?? null;
  }, [verificationsByItemKey]);

  return { loadVerifications, submitVerification, getItemData, submitting };
}
